export class SessionTracker {
  private sessionStart: Date | null = null;
  private lastActivity: Date = new Date();
  private totalMinutes: number = 0;
  private idleTimeout: NodeJS.Timeout | null = null;
  private sessionActive: boolean = false;
  private IDLE_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor() {
    // Listen for visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  startSession(): void {
    if (!this.sessionActive) {
      this.sessionStart = new Date();
      this.lastActivity = new Date();
      this.sessionActive = true;
      this.resetIdleTimer();
      console.log('Session started at:', this.sessionStart);
    }
  }

  trackActivity(): void {
    if (!this.sessionActive) {
      this.startSession();
      return;
    }

    const now = new Date();
    const timeSinceLastActivity = now.getTime() - this.lastActivity.getTime();
    
    // If it's been more than idle threshold, we had a break
    if (timeSinceLastActivity > this.IDLE_THRESHOLD) {
      // Add the time before the break to total
      const sessionTime = this.lastActivity.getTime() - (this.sessionStart?.getTime() || 0);
      this.totalMinutes += Math.floor(sessionTime / 60000);
      
      // Start a new session segment
      this.sessionStart = now;
    }
    
    this.lastActivity = now;
    this.resetIdleTimer();
  }

  private resetIdleTimer(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }

    this.idleTimeout = setTimeout(() => {
      this.pauseSession();
    }, this.IDLE_THRESHOLD);
  }

  private pauseSession(): void {
    if (this.sessionActive && this.sessionStart) {
      const sessionTime = this.lastActivity.getTime() - this.sessionStart.getTime();
      this.totalMinutes += Math.floor(sessionTime / 60000);
      this.sessionActive = false;
      console.log('Session paused. Total minutes:', this.totalMinutes);
    }
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.pauseSession();
    } else {
      // Don't auto-start, wait for user activity
      this.sessionActive = false;
    }
  };

  endSession(): number {
    if (this.sessionActive && this.sessionStart) {
      const sessionTime = this.lastActivity.getTime() - this.sessionStart.getTime();
      this.totalMinutes += Math.floor(sessionTime / 60000);
    }

    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }

    const finalMinutes = this.totalMinutes;
    
    // Reset for next session
    this.sessionStart = null;
    this.sessionActive = false;
    this.totalMinutes = 0;
    
    console.log('Session ended. Total minutes:', finalMinutes);
    return finalMinutes;
  }

  getTotalMinutes(): number {
    if (this.sessionActive && this.sessionStart) {
      const currentSessionTime = this.lastActivity.getTime() - this.sessionStart.getTime();
      return this.totalMinutes + Math.floor(currentSessionTime / 60000);
    }
    return this.totalMinutes;
  }

  cleanup(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
  }
}

// Singleton instance
export const sessionTracker = new SessionTracker();